import React from "react";
import { Helmet } from "react-helmet";
import "./Hosting.css";

const siteUrl = "https://www.samsoftix.com";
const pageUrl = `${siteUrl}/hosting`;
const hostingUrl = "https://hostinger.com?REFERRALCODE=E2CQASIMC7DN";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SamSoftix",
      url: siteUrl
    },
    {
      "@type": ["LocalBusiness", "ProfessionalService"],
      "@id": `${siteUrl}/#business`,
      name: "SamSoftix",
      url: siteUrl,
      telephone: "+92-332-0472184",
      email: "info@samsoftix.com",
      description:
        "SamSoftix provides website development, hosting setup, domain management, deployment, ecommerce, custom software and digital services.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Wan",
        addressRegion: "Punjab",
        addressCountry: "PK"
      },
      areaServed: [
        "Wan",
        "Sambrial",
        "Sialkot",
        "Punjab",
        "Pakistan",
        "United Arab Emirates",
        "Saudi Arabia",
        "United States",
        "United Kingdom"
      ]
    },
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: "Web Hosting & Domain Setup in Pakistan | SamSoftix",
      description:
        "Learn about web hosting, domain registration, SSL, deployment and Hostinger hosting options with SamSoftix.",
      isPartOf: {
        "@id": `${siteUrl}/#website`
      },
      about: {
        "@id": `${siteUrl}/#business`
      }
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Hosting",
          item: pageUrl
        }
      ]
    },
    {
      "@type": "Service",
      name: "Web Hosting & Domain Setup",
      provider: {
        "@id": `${siteUrl}/#business`
      },
      serviceType: [
        "Web Hosting",
        "Domain Registration",
        "SSL Setup",
        "Website Deployment",
        "Hosting Management"
      ],
      areaServed: [
        "Pakistan",
        "United Arab Emirates",
        "Saudi Arabia",
        "United States",
        "United Kingdom"
      ],
      url: pageUrl
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is web hosting?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Web hosting provides the server space and infrastructure required to make a website accessible on the internet."
          }
        },
        {
          "@type": "Question",
          name: "Can I buy a domain and hosting together?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. Eligible hosting plans may include a domain for the first year, depending on the hosting provider and selected plan."
          }
        },
        {
          "@type": "Question",
          name: "Is SSL included with hosting?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "SSL availability depends on the hosting plan. Many modern hosting plans include SSL as part of the package."
          }
        },
        {
          "@type": "Question",
          name: "Can SamSoftix help with hosting setup?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. SamSoftix can assist with domain setup, hosting configuration, SSL, website deployment and related technical setup."
          }
        }
      ]
    }
  ]
};

export default function Hosting() {
  return (
    <div className="hosting-page">
      <Helmet>
        <html lang="en-PK" />

        <title>
          Web Hosting & Domain Setup in Pakistan | Hostinger Offer | SamSoftix
        </title>

        <meta
          name="description"
          content="Web hosting, domain, SSL and website deployment support from SamSoftix. Explore Hostinger hosting options and get help setting up your website in Pakistan or remotely."
        />

        <meta
          name="keywords"
          content="web hosting Pakistan, domain registration Pakistan, hosting setup Pakistan, Hostinger Pakistan, website hosting, domain hosting, SSL setup, website deployment, SamSoftix hosting, hosting Sambrial, hosting Sialkot"
        />

        <meta name="author" content="SamSoftix" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />

        <link rel="canonical" href={pageUrl} />

        <meta name="geo.region" content="PK-PB" />
        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <meta
          property="og:title"
          content="Web Hosting & Domain Setup | SamSoftix"
        />

        <meta
          property="og:description"
          content="Web hosting, domain, SSL and website deployment support from SamSoftix."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="SamSoftix" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Web Hosting & Domain Setup | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Hosting, domain, SSL and deployment solutions for websites and businesses."
        />

        <meta name="theme-color" content="#06111f" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="hosting-hero">
        <div className="hosting-container">
          <span className="hosting-kicker">
            WEB HOSTING • DOMAIN • SSL • DEPLOYMENT
          </span>

          <h1>
            Web Hosting & Domain Solutions
            <span> for Businesses</span>
          </h1>

          <p>
            Get the hosting infrastructure your website needs, with domain,
            SSL and deployment support from SamSoftix.
          </p>

          <div className="hosting-location-tags">
            <span>Pakistan</span>
            <span>Sambrial</span>
            <span>Sialkot</span>
            <span>UAE</span>
            <span>Saudi Arabia</span>
            <span>Worldwide</span>
          </div>

          <div className="hosting-hero-actions">
            <a
              href={hostingUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="hosting-btn hosting-btn-primary"
            >
              Explore Hosting Options
            </a>

            <a
              href="/serviceBooking"
              className="hosting-btn hosting-btn-secondary"
            >
              Get Setup Help
            </a>
          </div>

          <p className="hosting-disclosure">
            Hosting provider and plan availability, pricing and promotional
            terms are determined by the hosting provider.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="hosting-section">
        <div className="hosting-container">
          <div className="hosting-section-heading">
            <span className="hosting-section-label">
              HOSTING SOLUTIONS
            </span>

            <h2>
              Hosting for Websites, Stores & Business Applications
            </h2>

            <p>
              Your website needs more than a domain name. It needs reliable
              hosting, SSL, proper deployment and a configuration suitable for
              the project. SamSoftix can help you choose and configure the
              required setup.
            </p>
          </div>

          <div className="hosting-feature-grid">
            <article className="hosting-feature-card">
              <span>01</span>
              <h3>Web Hosting</h3>
              <p>
                Hosting options for business websites, portfolios,
                WordPress projects and web applications.
              </p>
            </article>

            <article className="hosting-feature-card">
              <span>02</span>
              <h3>Domain Setup</h3>
              <p>
                Assistance with domain configuration, DNS records and
                connecting your domain with your website.
              </p>
            </article>

            <article className="hosting-feature-card">
              <span>03</span>
              <h3>SSL & Security</h3>
              <p>
                SSL configuration and basic hosting security setup for
                websites and online businesses.
              </p>
            </article>

            <article className="hosting-feature-card">
              <span>04</span>
              <h3>Website Deployment</h3>
              <p>
                Deploy React, Next.js, WordPress and other supported website
                projects to the appropriate hosting environment.
              </p>
            </article>

            <article className="hosting-feature-card">
              <span>05</span>
              <h3>Performance Setup</h3>
              <p>
                Configuration assistance for caching, CDN, compression and
                other available performance features.
              </p>
            </article>

            <article className="hosting-feature-card">
              <span>06</span>
              <h3>Technical Support</h3>
              <p>
                Get assistance when you need help connecting domains,
                deploying websites or configuring hosting.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* HOSTINGER OFFER */}
      <section className="hosting-offer-section">
        <div className="hosting-container">
          <div className="hosting-offer-card">
            <div className="hosting-offer-content">
              <span className="hosting-section-label">
                HOSTING OFFER
              </span>

              <h2>Explore Hostinger Hosting Options</h2>

              <p>
                If you are looking for hosting for a new website, you can
                explore Hostinger plans through our referral link.
              </p>

              <div className="hosting-code-box">
                <span>Referral Code</span>
                <strong>E2CQASIMC7DN</strong>
              </div>

              <a
                href={hostingUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="hosting-btn hosting-btn-primary"
              >
                Explore Hostinger
              </a>

              <p className="hosting-small-note">
                Any discount or promotional benefit depends on the applicable
                Hostinger offer, plan and eligibility requirements.
              </p>
            </div>

            <div className="hosting-offer-side">
              <div className="hosting-offer-icon">🌐</div>
              <h3>Hosting + Domain</h3>
              <p>
                Choose the hosting package according to your website,
                application and resource requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DOMAIN SECTION */}
      <section className="hosting-section hosting-domain-section">
        <div className="hosting-container">
          <div className="hosting-section-heading">
            <span className="hosting-section-label">
              UNDERSTANDING YOUR SETUP
            </span>

            <h2>Domain, Hosting, SSL & Deployment</h2>
          </div>

          <div className="hosting-domain-grid">
            <article className="hosting-domain-card">
              <h3>Domain</h3>
              <p>
                Your domain is the web address customers use to find your
                website.
              </p>
            </article>

            <article className="hosting-domain-card">
              <h3>Hosting</h3>
              <p>
                Hosting provides the server environment where your website
                files and application run.
              </p>
            </article>

            <article className="hosting-domain-card">
              <h3>SSL</h3>
              <p>
                SSL enables encrypted HTTPS connections between visitors and
                your website.
              </p>
            </article>

            <article className="hosting-domain-card">
              <h3>Deployment</h3>
              <p>
                Deployment connects your website project to the hosting
                environment so it can be accessed online.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* SAMSOFTIX SERVICES */}
      <section className="hosting-services-section">
        <div className="hosting-container">
          <div className="hosting-section-heading">
            <span className="hosting-section-label">
              SAMSOFTIX SERVICES
            </span>

            <h2>Need More Than Hosting?</h2>

            <p>
              SamSoftix can also help you build, deploy and maintain the
              website or software that runs on your hosting.
            </p>
          </div>

          <div className="hosting-services-grid">
            <div className="hosting-service-box">
              <h3>Website Development</h3>
              <p>
                Business websites, landing pages, web applications and
                custom websites.
              </p>
            </div>

            <div className="hosting-service-box">
              <h3>Ecommerce Development</h3>
              <p>
                Online stores using Shopify, WooCommerce or custom
                development.
              </p>
            </div>

            <div className="hosting-service-box">
              <h3>Custom Software</h3>
              <p>
                Business management systems, dashboards and custom web
                applications.
              </p>
            </div>

            <div className="hosting-service-box">
              <h3>Deployment & Maintenance</h3>
              <p>
                Deployment, configuration, updates and technical maintenance
                for supported projects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="hosting-section hosting-faq-section">
        <div className="hosting-container">
          <div className="hosting-section-heading">
            <span className="hosting-section-label">
              FAQ
            </span>

            <h2>Frequently Asked Questions</h2>
          </div>

          <div className="hosting-faq-list">
            <article className="hosting-faq-item">
              <h3>What is web hosting?</h3>
              <p>
                Web hosting provides the server space and infrastructure
                required to make a website accessible on the internet.
              </p>
            </article>

            <article className="hosting-faq-item">
              <h3>Can I buy a domain and hosting together?</h3>
              <p>
                Yes. Some hosting plans can include a domain for the first
                year, depending on the selected plan and current provider
                terms.
              </p>
            </article>

            <article className="hosting-faq-item">
              <h3>Is SSL included with hosting?</h3>
              <p>
                SSL availability depends on the hosting provider and plan.
                Many hosting packages include SSL.
              </p>
            </article>

            <article className="hosting-faq-item">
              <h3>Is hosting suitable for beginners?</h3>
              <p>
                Many hosting providers offer control panels and tools designed
                to make website management easier for beginners.
              </p>
            </article>

            <article className="hosting-faq-item">
              <h3>Can SamSoftix deploy my website?</h3>
              <p>
                Yes. You can contact SamSoftix for website deployment,
                domain configuration, hosting setup and related technical
                assistance.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="hosting-cta-section">
        <div className="hosting-container">
          <div className="hosting-cta-card">
            <span className="hosting-section-label">
              READY TO GO ONLINE?
            </span>

            <h2>
              Need Help With Your Website Hosting?
            </h2>

            <p>
              Contact SamSoftix for hosting setup, domain configuration,
              deployment or website development.
            </p>

            <div className="hosting-cta-actions">
              <a
                href="/serviceBooking"
                className="hosting-btn hosting-btn-primary"
              >
                Book a Service
              </a>

              <a
                href="https://wa.me/923320472184"
                target="_blank"
                rel="noopener noreferrer"
                className="hosting-btn hosting-btn-secondary"
              >
                WhatsApp SamSoftix
              </a>
            </div>

            <p className="hosting-contact-line">
              Wan • Sambrial • Sialkot • Pakistan • Remote Worldwide
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}